import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import PhotographerService from "services/PhotographerService";

export const accessStudentProfile = createAsyncThunk(
  "school/accessStudentProfile",
  async (params, { rejectWithValue }) => {
    try {
      const response = await PhotographerService.accessStudentProfile(params);
      return response;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch academic year"
      );
    }
  }
);

export const savePhotosWithPoses = createAsyncThunk(
  "photoGraph/savePhotosWithPoses",
  async (photosData, { rejectWithValue }) => {
    try {
      const response = await PhotographerService.savePhotosWithPoses(
        photosData
      );
      return response;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to save photos"
      );
    }
  }
);

export const initialState = {
  loading: false,
  message: "",
  studentProfile: {},
  selectedFiles: [],
  studentFolders: {},
  filePoses: {},
  uploadProgress: 0,
  validationErrors: [],
  saveLoading: false,
  saveSuccess: false,
  currentStudent: null,
  // Add form data to store upload parameters
  formData: {
    school_id: null,
    class_id: null,
    division_id: null,
    academic_year_id: null,
    country_id: null,
  },
};

export const photoGrapherSlice = createSlice({
  name: "photoGraph",
  initialState,
  reducers: {
    setPageView(state, action) {
      state.pageView = action.payload;
    },
    setSelectedFiles(state, action) {
      state.selectedFiles = action.payload;
    },
    setStudentFolders(state, action) {
      state.studentFolders = action.payload;
    },
    addStudentFolder(state, action) {
      const { studentName, files } = action.payload;
      state.studentFolders[studentName] = files;
    },
    setFilePoses(state, action) {
      state.filePoses = action.payload;
    },
    updateFilePose(state, action) {
      const { fileName, pose } = action.payload;
      state.filePoses[fileName] = pose;
    },
    removeFileFromState(state, action) {
      const { fileName, fileIndex } = action.payload;

      state.selectedFiles = state.selectedFiles.filter(
        (_, index) => index !== fileIndex
      );

      delete state.filePoses[fileName];

      Object.keys(state.studentFolders).forEach((studentName) => {
        state.studentFolders[studentName] = state.studentFolders[
          studentName
        ].filter((file) => file.name !== fileName);

        if (state.studentFolders[studentName].length === 0) {
          delete state.studentFolders[studentName];
        }
      });
    },
    setCurrentStudent(state, action) {
      state.currentStudent = action.payload;
    },
    setUploadProgress(state, action) {
      state.uploadProgress = action.payload;
    },
    setValidationErrors(state, action) {
      state.validationErrors = action.payload;
    },
    clearValidationErrors(state) {
      state.validationErrors = [];
    },
    clearSelectedFiles(state) {
      state.selectedFiles = [];
    },
    clearStudentFolders(state) {
      state.studentFolders = {};
    },
    clearFilePoses(state) {
      state.filePoses = {};
    },
    resetPhotoState(state) {
      state.selectedFiles = [];
      state.studentProfile = {};
      state.studentFolders = {};
      state.filePoses = {};
      state.validationErrors = [];
      state.uploadProgress = 0;
      state.saveSuccess = false;
      state.currentStudent = null;
      state.formData = {
        school_id: null,
        class_id: null,
        division_id: null,
        academic_year_id: null,
        country_id: null,
      };
    },
    setSaveSuccess(state, action) {
      state.saveSuccess = action.payload;
    },
    // Add new reducer to set form data
    setFormData(state, action) {
      state.formData = {
        ...state.formData,
        ...action.payload,
      };
    },
    // Add reducer to update individual form field
    updateFormField(state, action) {
      const { field, value } = action.payload;
      state.formData[field] = value;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(accessStudentProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(accessStudentProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.studentProfile = action.payload.data
      })
      .addCase(accessStudentProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(savePhotosWithPoses.pending, (state) => {
        state.saveLoading = true;
        state.error = null;
      })
      .addCase(savePhotosWithPoses.fulfilled, (state, action) => {
        state.saveLoading = false;
        state.saveSuccess = true;
        state.message = "Photos saved successfully";
      })
      .addCase(savePhotosWithPoses.rejected, (state, action) => {
        state.saveLoading = false;
        state.error = action.payload;
        state.saveSuccess = false;
      });
  },
});

export const {
  setPageView,
  setSelectedFiles,
  setStudentFolders,
  addStudentFolder,
  setFilePoses,
  updateFilePose,
  removeFileFromState,
  setCurrentStudent,
  setUploadProgress,
  setValidationErrors,
  clearValidationErrors,
  clearSelectedFiles,
  clearStudentFolders,
  clearFilePoses,
  resetPhotoState,
  setSaveSuccess,
  setFormData,
  updateFormField,
} = photoGrapherSlice.actions;

export default photoGrapherSlice.reducer;
